import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import VideoWindow from './VideoWindow'
import MyPreview from './MyPreview'
import CallControls from './CallControls'
import { useWebRTC } from './userWebRTC'

/**
 * 키오스크용 간소화된 VideoChat 컴포넌트
 * - 단일 버튼으로 통화 요청/종료
 * - 작은 크기 (120x160px)
 * - 초기 화면에 이미지 표시
 *
 * 관리자용은 AdminVideoChat 컴포넌트를 사용하세요.
 */
const VideoChat = ({ userType = 'user', onCallEnded, socket, initialLocalStream = null }) => {
  console.log('VideoChat 컴포넌트 렌더링 (키오스크용), userType:', userType)

  const {
    localStream,
    remoteStream,
    callState,
    isIncomingCall,
    queuePosition,
    startCall,
    endCall,
    acceptCall,
    rejectCall,
    cancelCall,
  } = useWebRTC(userType, socket, onCallEnded, initialLocalStream)

  const handleStartCall = () => {
    console.log('통화 요청 버튼 클릭됨')
    startCall()
  }

  const handleEndCall = () => {
    console.log('통화 종료 버튼 클릭됨')
    endCall()
  }

  const handleAcceptCall = () => {
    console.log('통화 수락 버튼 클릭됨')
    acceptCall()
  }

  const handleRejectCall = () => {
    console.log('통화 거절 버튼 클릭됨')
    rejectCall()
  }

  const handleCancelCall = () => {
    console.log('통화 취소 버튼 클릭됨')
    cancelCall()
  }

  // 대기열 위치 메시지 생성
  const getQueueMessage = () => {
    if (queuePosition) {
      return `대기열 ${queuePosition}번째 - 관리자 응답 대기중...`
    }
    return '대기열 등록 중...'
  }

  // 키오스크용 간소화된 UI 상태 결정
  const getButtonText = () => {
    if (callState === 'active') return '통화끊기'
    if (callState === 'waiting_queue' || callState === 'calling') return '통화연결중...'
    return '화상통화'
  }

  const getButtonClass = () => {
    if (callState === 'active') return 'vc-btn vc-btn-end'
    if (callState === 'waiting_queue' || callState === 'calling') return 'vc-btn vc-btn-connecting'
    return 'vc-btn vc-btn-start-kiosk'
  }

  const handleButtonClick = () => {
    if (callState === 'active') {
      handleEndCall()
    } else if (callState === 'waiting_queue' || callState === 'calling') {
      handleCancelCall()
    } else if (isIncomingCall) {
      handleAcceptCall()
    } else {
      handleStartCall()
    }
  }

  // 키오스크용 비디오 ref
  const kioskVideoRef = useRef(null)

  // 키오스크용 비디오 스트림 설정
  useEffect(() => {
    if (kioskVideoRef.current && remoteStream) {
      console.log('키오스크 비디오 스트림 설정:', remoteStream)
      kioskVideoRef.current.srcObject = remoteStream
      kioskVideoRef.current.play().catch((error) => {
        console.error('키오스크 비디오 재생 실패:', error)
      })
    }
  }, [remoteStream])

  return (
    <div className="vc-container">
      {/* 헤더 제거 */}
      <div className="vc-video-container">
        {/* 통화 중이 아닐 때 이미지 표시 */}
        {!remoteStream && callState !== 'active' && (
          <div className="vc-kiosk-image">
            <img src="/images/pwan/main_pwan_02.png" alt="화상통화 대기" />
          </div>
        )}
        {/* 통화 중일 때 상대방 비디오 표시 - 키오스크용 직접 video 태그 */}
        {(remoteStream || callState === 'active') && (
          <video
            ref={kioskVideoRef}
            className="vc-video-window"
            autoPlay
            playsInline
            muted={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '6px',
            }}
          />
        )}
        {/* 비디오가 없을 때 플레이스홀더 */}
        {!remoteStream && callState === 'active' && (
          <div className="vc-video-placeholder">상대방 연결 대기중...</div>
        )}
      </div>
      {/* 내 카메라 미리보기 */}
      {/* {callState === 'active' && (
        <div className="vc-my-preview">
          <MyPreview stream={localStream} />
        </div>
      )} */}
      {/* 단일 버튼 */}
      <div className="vc-controls-row">
        <button
          className={getButtonClass()}
          onClick={handleButtonClick}
          disabled={callState === 'waiting_queue' || callState === 'calling'}
        >
          {getButtonText()}
        </button>
      </div>
    </div>
  )
}

VideoChat.propTypes = {
  userType: PropTypes.string,
  remoteStream: PropTypes.any,
  localStream: PropTypes.any,
  callState: PropTypes.string,
  onStartCall: PropTypes.func,
  onEndCall: PropTypes.func,
  onCallEnded: PropTypes.func,
  socket: PropTypes.any,
  initialLocalStream: PropTypes.any,
}

export default VideoChat
